import { useState } from 'react';
import { Clock, PenLine, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input, Tooltip, useToast } from '@waymail/ui';
import { horarioValido, paraCampoLocal } from '../lib/agendamento';
import { ErroDaApi } from '../lib/api';
import {
  apagarNoServidor,
  cancelarEnvio,
  chavesDeRascunho,
  mudarHorario,
  useAgendadas,
  useRascunhos,
  type Agendada,
  type ResumoDeRascunho,
} from '../lib/rascunhoServidor';
import { useUi } from '../store/ui';

/**
 * Rascunhos guardados no servidor (ADR 0052), os mesmos em qualquer aparelho.
 * Aparece na pasta Rascunhos; abrir um rascunho o entrega ao composer.
 */

function quando(iso: string): string {
  const data = new Date(iso);
  const hoje = new Date();
  const mesmoDia = data.toDateString() === hoje.toDateString();
  return mesmoDia
    ? data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

const dataEHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Envios agendados que ainda esperam a hora (ADR 0066), acima dos rascunhos.
 * Cancelar devolve a mensagem para Rascunhos; nada e descartado.
 */
function Agendadas({ accountId }: { accountId: string | undefined }) {
  const agendadas = useAgendadas(accountId);
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editando, setEditando] = useState<string | null>(null);
  const [horario, setHorario] = useState('');
  const [ocupado, setOcupado] = useState<string | null>(null);

  const itens = agendadas.data ?? [];
  if (itens.length === 0) return null;

  async function agir(id: string, acao: () => Promise<void>) {
    setOcupado(id);
    try {
      await acao();
    } catch (e) {
      toast({
        title: e instanceof ErroDaApi ? e.message : 'Não foi possível concluir. Confira a conexão.',
        tone: 'error',
      });
    } finally {
      setOcupado(null);
      await queryClient.invalidateQueries({ queryKey: chavesDeRascunho.lista });
    }
  }

  const cancelar = (a: Agendada) =>
    agir(a.id, async () => {
      await cancelarEnvio(a.id);
      toast({ title: 'Agendamento cancelado.', description: 'A mensagem voltou para Rascunhos.' });
    });

  const salvarHorario = (a: Agendada, quando: Date) =>
    agir(a.id, async () => {
      await mudarHorario(a.id, quando);
      setEditando(null);
      toast({
        title: 'Horário alterado.',
        description: `A mensagem sai em ${dataEHora(quando.toISOString())}.`,
      });
    });

  const escolhido = horario === '' ? null : new Date(horario);

  return (
    <div role="group" aria-label="Agendadas" className="border-b border-subtle pb-2">
      <h2 className="px-4 pt-4 pb-2 text-[14px] font-semibold text-primary">Agendadas</h2>
      <ul className="flex flex-col">
        {itens.map((a) => {
          const assunto = a.assunto.trim() === '' ? '(sem assunto)' : a.assunto;
          return (
            <li key={a.id} className="flex flex-col gap-2 px-4 py-2.5">
              <div className="flex min-w-0 items-center gap-3">
                <Clock
                  size={16}
                  strokeWidth={1.5}
                  aria-hidden="true"
                  className="shrink-0 text-tertiary"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-primary">
                    {assunto}
                  </span>
                  <span className="block truncate text-[12px] text-tertiary">
                    Para {a.para.join(', ')} · sai em {dataEHora(a.scheduledAt)}
                  </span>
                </span>
              </div>
              {editando === a.id ? (
                <div className="flex flex-wrap items-center gap-2 pl-7">
                  <Input
                    type="datetime-local"
                    aria-label={`Novo horário de ${assunto}`}
                    value={horario}
                    min={paraCampoLocal(new Date())}
                    onChange={(e) => setHorario(e.target.value)}
                    className="w-auto"
                  />
                  <Button
                    size="sm"
                    loading={ocupado === a.id}
                    disabled={escolhido === null || !horarioValido(escolhido)}
                    onClick={() => {
                      if (escolhido !== null) void salvarHorario(a, escolhido);
                    }}
                  >
                    Salvar horário
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditando(null)}>
                    Voltar
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2 pl-7">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Alterar horário de ${assunto}`}
                    onClick={() => {
                      setHorario(paraCampoLocal(new Date(a.scheduledAt)));
                      setEditando(a.id);
                    }}
                  >
                    Alterar horário
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Cancelar envio de ${assunto}`}
                    loading={ocupado === a.id}
                    onClick={() => void cancelar(a)}
                  >
                    Cancelar envio
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function ListaDeRascunhos({
  accountId,
  /** Dentro da pasta Rascunhos da caixa, divide a coluna com as mensagens dela. */
  compartilhada,
}: {
  accountId: string | undefined;
  compartilhada: boolean;
}) {
  const rascunhos = useRascunhos(accountId, true);
  const abrir = useUi((s) => s.abrirRascunho);
  const queryClient = useQueryClient();
  const toast = useToast();

  async function apagar(r: ResumoDeRascunho) {
    try {
      await apagarNoServidor(r.id);
    } catch (e) {
      // 404: outro aparelho ja apagou, e a lista so precisa se atualizar.
      if (!(e instanceof ErroDaApi && e.status === 404)) {
        toast({ title: 'Não foi possível apagar o rascunho.', tone: 'error' });
        return;
      }
    }
    await queryClient.invalidateQueries({ queryKey: chavesDeRascunho.lista });
  }

  const itens = rascunhos.data ?? [];

  return (
    <section
      aria-label="Rascunhos"
      className={
        compartilhada
          ? 'flex max-h-[60%] w-full shrink-0 flex-col overflow-y-auto border-b border-subtle bg-app'
          : 'flex w-full flex-col overflow-y-auto bg-app'
      }
    >
      <Agendadas accountId={accountId} />

      <h2 className="px-4 pt-4 pb-2 text-[14px] font-semibold text-primary">Rascunhos</h2>

      {rascunhos.isError ? (
        <p role="alert" className="px-4 pb-4 text-[13px] text-error">
          Não foi possível carregar os rascunhos. Confira a conexão.
        </p>
      ) : rascunhos.isPending ? (
        <p className="px-4 pb-4 text-[13px] text-tertiary">Carregando rascunhos...</p>
      ) : itens.length === 0 ? (
        <p className="px-4 pb-4 text-[13px] text-tertiary">Nenhum rascunho salvo.</p>
      ) : (
        <ul className="flex flex-col pb-2">
          {itens.map((r) => (
            <li key={r.id} className="group/rascunho relative flex items-center">
              <button
                type="button"
                onClick={() => abrir(r.id)}
                className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 px-4 py-2.5 text-left hover:bg-hover"
              >
                <PenLine
                  size={16}
                  strokeWidth={1.5}
                  aria-hidden="true"
                  className="shrink-0 text-tertiary"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-primary">
                    {r.subject.trim() === '' ? '(sem assunto)' : r.subject}
                  </span>
                  <span className="block truncate text-[12px] text-tertiary">
                    {r.snippet === '' ? 'Sem texto' : r.snippet}
                  </span>
                </span>
                <span className="shrink-0 pr-9 text-[11px] text-tertiary">
                  {quando(r.updatedAt)}
                </span>
              </button>
              <Tooltip content="Apagar rascunho">
                <button
                  type="button"
                  onClick={() => void apagar(r)}
                  aria-label={`Apagar rascunho ${r.subject.trim() === '' ? 'sem assunto' : r.subject}`}
                  className="absolute right-2 flex size-8 cursor-pointer items-center justify-center rounded-lg text-tertiary hover:bg-hover hover:text-error"
                >
                  <Trash2 size={15} strokeWidth={1.8} aria-hidden="true" />
                </button>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
